/**
 * 统一认证组合式函数
 * 提供用户认证状态和方法
 */

import { computed, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { authManager } from '@/auth'
import { initializePermissionManager } from '@/utils/permission-utils'

/**
 * 用户信息接口
 */
export interface IUserInfo {
  id: string | number
  username: string
  nickname?: string
  email?: string
  phone?: string
  avatar?: string
  roles: string[]
  permissions: string[]
  tenantId?: string
  tenantName?: string
  lastLoginAt?: string
  status: 'active' | 'inactive' | 'locked'
}

/**
 * 登录参数接口
 */
export interface ILoginParams {
  username: string
  password: string
  captcha?: string
  rememberMe?: boolean
  tenantId?: string
}

/**
 * 认证状态
 */
const isAuthenticated = ref<boolean>(false)
const userInfo = ref<IUserInfo | null>(null)
const loading = ref<boolean>(false)

/**
 * 认证相关的组合式函数
 */
export const useAuth = () => {
  const router = useRouter()

  /**
   * 是否已登录
   */
  const isLoggedIn = computed(() => isAuthenticated.value && !!userInfo.value)

  /**
   * 当前用户信息
   */
  const currentUser = computed(() => userInfo.value)

  /**
   * 用户角色列表
   */
  const userRoles = computed(() => userInfo.value?.roles || [])

  /**
   * 用户权限列表
   */
  const userPermissions = computed(() => userInfo.value?.permissions || [])

  /**
   * 是否为超级管理员
   */
  const isSuperAdmin = computed(() => {
    return userRoles.value.includes('super_admin') || userRoles.value.includes('admin')
  })

  /**
   * 是否为租户管理员
   */
  const isTenantAdmin = computed(() => {
    return userRoles.value.includes('tenant_admin')
  })

  // 初始化权限管理器
  onMounted(async () => {
    if (isAuthenticated.value) {
      await initializePermissionManager()
    }
  })

  /**
   * 登录
   * @param params 登录参数
   */
  const login = async (params: ILoginParams): Promise<void> => {
    try {
      loading.value = true

      const result = await authManager.login(params)

      isAuthenticated.value = true
      userInfo.value = result.user as unknown as IUserInfo

      await initializePermissionManager()

      ElMessage.success('登录成功')

      await router.push('/home')
    } catch (error: any) {
      ElMessage.error(error.message || '登录失败，请检查用户名和密码')
      throw error
    } finally {
      loading.value = false
    }
  }

  // 注意：logout方法已移除，统一使用 stores/user.ts 中的 logout 方法

  /**
   * 刷新用户信息
   */
  const refreshUserInfo = async (): Promise<void> => {
    try {
      const state = authManager.getAuthState()
      if (state.user && state.isAuthenticated) {
        userInfo.value = state.user as unknown as IUserInfo
        isAuthenticated.value = true
      } else {
        userInfo.value = null
        isAuthenticated.value = false
      }
    } catch (error) {
      console.error('刷新用户信息失败:', error)
    }
  }

  /**
   * 检查是否有指定权限
   * @param permission 权限标识
   */
  const hasPermission = (permission: string | string[]): boolean => {
    if (!isLoggedIn.value) return false

    if (isSuperAdmin.value) return true

    if (typeof permission === 'string') {
      return userPermissions.value.includes(permission)
    }

    if (Array.isArray(permission)) {
      return permission.some(p => userPermissions.value.includes(p))
    }

    return false
  }

  /**
   * 检查是否有指定角色
   * @param role 角色标识
   */
  const hasRole = (role: string | string[]): boolean => {
    if (!isLoggedIn.value) return false

    if (typeof role === 'string') {
      return userRoles.value.includes(role)
    }

    if (Array.isArray(role)) {
      return role.some(r => userRoles.value.includes(r))
    }

    return false
  }

  /**
   * 检查是否有所有指定权限
   * @param permissions 权限列表
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    if (!isLoggedIn.value) return false
    if (isSuperAdmin.value) return true

    return permissions.every(p => userPermissions.value.includes(p))
  }

  /**
   * 检查是否有任一指定权限
   * @param permissions 权限列表
   */
  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!isLoggedIn.value) return false
    if (isSuperAdmin.value) return true

    return permissions.some(p => userPermissions.value.includes(p))
  }

  /**
   * 初始化认证状态
   */
  const initAuth = async (): Promise<void> => {
    try {
      // 检查是否有有效的token
      const token = authManager.getCurrentToken()
      if (token) {
        await refreshUserInfo()
      }
    } catch (error) {
      console.error('初始化认证状态失败:', error)
    }
  }

  /**
   * 更新用户信息
   * @param updates 要更新的用户信息
   */
  const updateUserInfo = (updates: Partial<IUserInfo>): void => {
    if (userInfo.value) {
      userInfo.value = { ...userInfo.value, ...updates }
    }
  }

  return {
    // 状态
    isLoggedIn,
    currentUser,
    userRoles,
    userPermissions,
    isSuperAdmin,
    isTenantAdmin,
    loading,

    // 方法
    login,
    // logout 方法已移除，请使用 stores/user.ts 中的 logout 方法
    refreshUserInfo,
    hasPermission,
    hasRole,
    hasAllPermissions,
    hasAnyPermission,
    initAuth,
    updateUserInfo
  }
}

// 导出默认实例
export default useAuth
